import { useState, useEffect } from 'react';
import { Lobby } from '@/components/Lobby';
import { Game } from '@/components/Game';
import { Modal } from '@/components/Modal';
import { api } from '@/utils/api';
import { LanguageProvider, useLanguage } from '@/contexts/LanguageContext';
import { LanguageSelector } from '@/components/LanguageSelector';

function AppContent() {
    const { t } = useLanguage();
    const [sessionId, setSessionId] = useState<string>('');
    const [playerName, setPlayerName] = useState<string>('');
    const [isAuthReady, setIsAuthReady] = useState(false);
    const [currentPage, setCurrentPage] = useState<'lobby' | 'game' | 'name-input'>('name-input');
    const [currentGameId, setCurrentGameId] = useState<string>('');
    const [inputName, setInputName] = useState<string>('');

    // モーダル関連の状態
    const [modalMessage, setModalMessage] = useState<string | null>(null);
    const [modalCopyText, setModalCopyText] = useState<string>('');
    const [showModalCopyButton, setShowModalCopyButton] = useState(false);

    const getSessionStorageKeys = () => {
        const params = new URLSearchParams(window.location.search);
        const clientId = params.get('client')?.trim();
        if (clientId) {
            return {
                sessionIdKey: `reversi-sessionId-${clientId}`,
                playerNameKey: `reversi-playerName-${clientId}`,
            };
        }
        return {
            sessionIdKey: 'sessionId',
            playerNameKey: 'playerName',
        };
    };

    useEffect(() => {
        const initializeSession = async () => {
            try {
                const params = new URLSearchParams(window.location.search);
                const { sessionIdKey, playerNameKey } = getSessionStorageKeys();
                const gameIdFromUrl = params.get('gameId');

                const isLocalhost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname);
                const allowDevParams = import.meta.env.DEV || isLocalhost;

                let storedSessionId = localStorage.getItem(sessionIdKey);
                let storedPlayerName = localStorage.getItem(playerNameKey);

                if (allowDevParams) {
                    const devSessionId = params.get('devSessionId')?.trim();
                    const devName = params.get('devName')?.trim();

                    if (devSessionId && devName) {
                        storedSessionId = devSessionId;
                        storedPlayerName = devName;
                        localStorage.setItem(sessionIdKey, devSessionId);
                        localStorage.setItem(playerNameKey, devName);
                    } else if (devName && (!storedSessionId || !storedPlayerName)) {
                        const response = await api.createSession(devName);
                        if (response.success) {
                            storedSessionId = response.data.sessionId;
                            storedPlayerName = response.data.name;
                            localStorage.setItem(sessionIdKey, storedSessionId);
                            localStorage.setItem(playerNameKey, storedPlayerName);
                        }
                    }

                    if (devSessionId || devName) {
                        const newUrl = new URL(window.location.href);
                        newUrl.searchParams.delete('devSessionId');
                        newUrl.searchParams.delete('devName');
                        window.history.replaceState({}, '', newUrl.toString());
                    }
                }

                if (storedSessionId && storedPlayerName) {
                    setSessionId(storedSessionId);
                    setPlayerName(storedPlayerName);

                    if (gameIdFromUrl) {
                        // URLでゲームIDが指定されている場合、自動的にゲームに参加
                        try {
                            const joinResponse = await api.joinGame(gameIdFromUrl, storedSessionId);
                            if (joinResponse.success) {
                                setCurrentGameId(gameIdFromUrl);
                                setCurrentPage('game');
                                showModal('ゲームに参加しました！');
                            } else {
                                // 参加に失敗した場合、観戦モードとして表示
                                setCurrentGameId(gameIdFromUrl);
                                setCurrentPage('game');
                                showModal('ゲームに参加できませんでしたが、観戦モードで表示します。');
                            }
                        } catch (error) {
                            console.error('ゲーム参加エラー:', error);
                            setCurrentGameId(gameIdFromUrl);
                            setCurrentPage('game');
                            showModal('ゲームに参加できませんでしたが、観戦モードで表示します。');
                        }
                    } else {
                        setCurrentPage('lobby');
                    }
                } else {
                    setCurrentPage('name-input');
                }
            } catch (error) {
                console.error('セッション初期化エラー:', error);
                showModal('セッションの初期化に失敗しました。ページをリロードしてください。');
            } finally {
                setIsAuthReady(true);
            }
        };

        initializeSession();
    }, []);

    const createSession = async () => {
        if (!inputName.trim()) {
            showModal(t('error.enterPlayerName'));
            return;
        }

        try {
            const { sessionIdKey, playerNameKey } = getSessionStorageKeys();
            const response = await api.createSession(inputName.trim());

            if (response.success) {
                setSessionId(response.data.sessionId);
                setPlayerName(response.data.name);

                // ローカルストレージに保存
                localStorage.setItem(sessionIdKey, response.data.sessionId);
                localStorage.setItem(playerNameKey, response.data.name);

                // URLパラメータからゲームIDを確認
                const params = new URLSearchParams(window.location.search);
                const gameIdFromUrl = params.get('gameId');

                if (gameIdFromUrl) {
                    // URLでゲームIDが指定されている場合、自動的にゲームに参加
                    try {
                        const joinResponse = await api.joinGame(gameIdFromUrl, response.data.sessionId);
                        if (joinResponse.success) {
                            setCurrentGameId(gameIdFromUrl);
                            setCurrentPage('game');
                            showModal('ゲームに参加しました！');
                        } else {
                            // 参加に失敗した場合、観戦モードとして表示
                            setCurrentGameId(gameIdFromUrl);
                            setCurrentPage('game');
                            showModal('ゲームに参加できませんでしたが、観戦モードで表示します。');
                        }
                    } catch (error) {
                        console.error('ゲーム参加エラー:', error);
                        setCurrentGameId(gameIdFromUrl);
                        setCurrentPage('game');
                        showModal('ゲームに参加できませんでしたが、観戦モードで表示します。');
                    }
                } else {
                    setCurrentPage('lobby');
                }
            } else {
                throw new Error(response.error || 'セッション作成に失敗しました');
            }
        } catch (error) {
            console.error('セッション作成エラー:', error);
            showModal('セッションの作成に失敗しました。もう一度試してください。');
        }
    };

    const showModal = (message: string, showCopy = false, textToCopy = '') => {
        setModalMessage(message);
        setShowModalCopyButton(showCopy);
        setModalCopyText(textToCopy);
    };

    const closeModal = () => {
        setModalMessage(null);
        setShowModalCopyButton(false);
        setModalCopyText('');
    };

    const navigateToGame = (gameId: string) => {
        setCurrentGameId(gameId);
        setCurrentPage('game');
        // URLを更新
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('gameId', gameId);
        window.history.pushState({}, '', newUrl.toString());
    };

    const navigateToLobby = () => {
        setCurrentGameId('');
        setCurrentPage('lobby');
        // URLパラメータを削除
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('gameId');
        window.history.pushState({}, '', newUrl.toString());
    };

    if (!isAuthReady) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="absolute top-4 right-4">
                    <LanguageSelector />
                </div>
                <p className="text-lg font-semibold text-slate-700">{t('common.initializing')}</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-slate-50">
            <Modal
                message={modalMessage}
                onClose={closeModal}
                showCopyButton={showModalCopyButton}
                textToCopy={modalCopyText}
            />

            {currentPage === 'name-input' ? (
                <div className="relative">
                    <div className="absolute top-4 right-4">
                        <LanguageSelector />
                    </div>
                    <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg w-full max-w-md text-center mt-16">
                        <h1 className="text-3xl font-bold mb-6 text-slate-900">{t('game.title')}</h1>
                        <div className="mb-4">
                            <label htmlFor="playerName" className="block text-sm font-medium text-slate-700 mb-2">
                                {t('game.enterPlayerName')}
                            </label>
                            <input
                                id="playerName"
                                type="text"
                                value={inputName}
                                onChange={(e) => setInputName(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && createSession()}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder={t('game.yourNamePlaceholder')}
                                maxLength={20}
                            />
                        </div>
                        <button
                            onClick={createSession}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-95"
                        >
                            {t('game.startGame')}
                        </button>
                    </div>
                </div>
            ) : currentPage === 'lobby' ? (
                <Lobby
                    sessionId={sessionId}
                    playerName={playerName}
                    onNavigateToGame={navigateToGame}
                    showModal={showModal}
                />
            ) : (
                <Game
                    sessionId={sessionId}
                    gameId={currentGameId}
                    onNavigateToLobby={navigateToLobby}
                    showModal={(message: string) => showModal(message)}
                />
            )}
        </div>
    );
}

function App() {
    return (
        <LanguageProvider>
            <AppContent />
        </LanguageProvider>
    );
}

export default App; 
